import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Drawer, List, ListItem, ListItemText, ListItemSecondaryAction, ListSubheader, Typography, IconButton,
    Divider, CircularProgress, Chip, Box, Tooltip, Button, TextField, InputAdornment, Link
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import PlaylistAddIcon from '@material-ui/icons/PlaylistAdd';
import { getFieldsByRoute } from '../../../services/dataDictionaryService';
import { addFieldsToBasket } from '../../../redux/common/dataDictionaryBasket/action';
import BasketPinButton from './BasketPinButton';
import { scanOnScreenLabels, buildI18nLabelIndex, resolveFieldLabels } from './fieldUiLabels';
import { optionsSourceShortLabel, optionsSourceText, optionsSourceHref } from './optionsSource';

// Pinned-subheader pattern: each table group gets its own nested <ul> so its ListSubheader is sticky only within that group and pushes out the previous one (instead of stacking on top of it).
const useStyles = makeStyles((theme) => ({
    section: { backgroundColor: 'inherit', listStyle: 'none' },
    // listStyle: none — this is a raw <ul> (not a MUI List), so browser default
    // markers would otherwise show as hollow circles next to each field row.
    sectionList: { backgroundColor: 'inherit', padding: 0, listStyle: 'none' },
    subheader: {
        backgroundColor: theme.palette.background.paper,
        fontSize: '1rem',
        fontWeight: 600,
    },
}));

/** Qualified table label; the default 'dbo' schema is omitted to keep it quiet. */
const tableLabel = (f) =>
    f.schema && f.schema.toLowerCase() !== 'dbo' ? `${f.schema}.${f.table}` : f.table;

// Group fields by their source table, preserving order.
const groupByTable = (fields) => {
    const groups = [];
    let current = null;
    fields.forEach((f) => {
        if (!current || current.table !== f.table || current.schema !== f.schema) {
            current = { table: f.table, schema: f.schema, label: tableLabel(f), fields: [] };
            groups.push(current);
        }
        current.fields.push(f);
    });
    return groups;
};

// Slide-out panel showing the data dictionary for the CURRENT page.
const DataDictionaryPanel = ({ open, onClose }) => {
    const classes = useStyles();
    const location = useLocation();
    const history = useHistory();
    const dispatch = useDispatch();
    // Loading the namespaces here also lazy-fetches them via the http-backend, so the i18n fallback fills in once they arrive (the effect below re-runs on readiness).
    const { i18n } = useTranslation(['tableHeadCells', 'translation'], { useSuspense: false });
    const panelRef = useRef(null); // scrape-exclude root, so the panel's own search box isn't scraped
    const [fields, setFields] = useState([]);
    const [decorated, setDecorated] = useState([]); // fields + { uiLabel, uiLabelSource, onScreen }
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');

    // Jump to the Data Dictionary's Relationships & Diagram tab, focused on this table (the diagram defaults to table level at depth 1).
    const openDiagram = (table) => {
        onClose();
        history.push('/tools/data-dictionary', { dictionaryDiagramTable: table });
    };

    useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;
        setSearchText('');
        setLoading(true);
        getFieldsByRoute(location.pathname)
            .then((res) => { if (!cancelled) setFields(res.data || []); })
            .catch(() => { if (!cancelled) setFields([]); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, location.pathname]);

    // Resolve each field's UI label once the panel is open and fields are loaded.
    const headCellsReady = i18n.hasResourceBundle && i18n.hasResourceBundle(i18n.language || 'en-US', 'tableHeadCells');
    useEffect(() => {
        if (!open || fields.length === 0) { setDecorated(fields); return undefined; }
        // Show the loaded fields immediately (unlabelled, all under "other"); enrich with
        // UI labels + on-screen flags next frame, once the page behind has laid out.
        setDecorated(fields);
        let cancelled = false;
        const raf = requestAnimationFrame(() => {
            if (cancelled) return;
            const domMap = scanOnScreenLabels(panelRef.current);
            const i18nIndex = buildI18nLabelIndex(i18n, location.pathname);
            setDecorated(resolveFieldLabels(fields, domMap, i18nIndex));
        });
        return () => { cancelled = true; cancelAnimationFrame(raf); };
    }, [open, fields, i18n, headCellsReady, location.pathname]);

    // Client-side filter over the already-loaded fields (column / UI label / description /
    // lookup decode / table). On-screen fields are surfaced in their own section above the rest.
    const term = searchText.trim().toLowerCase();
    const visible = decorated.filter((f) =>
        !term || [f.column, f.uiLabel, f.description, f.lookupDecode, f.table,
            f.optionsSource && f.optionsSource.glossaryCategoryName,
            f.optionsSource && f.optionsSource.referenceName]
            .some((v) => v && v.toString().toLowerCase().includes(term)));
    const onScreenFields = visible.filter((f) => f.onScreen);
    const otherFields = visible.filter((f) => !f.onScreen);

    // One section's worth of fields, grouped by table (subheader + basket/diagram actions).
    const fieldList = (list) => (
        <List dense subheader={<li />}>
            {groupByTable(list).map((group) => (
                <li key={`${group.schema}.${group.table}`} className={classes.section}>
                    <ul className={classes.sectionList}>
                        <ListSubheader disableGutters color="primary" className={classes.subheader}>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <span>{group.label}</span>
                                <span>
                                    <Tooltip title={`Add all ${group.label} fields to basket`} arrow>
                                        <IconButton
                                            size="small"
                                            aria-label={`add all ${group.label} fields to basket`}
                                            onClick={() => dispatch(addFieldsToBasket(group.fields))}
                                        >
                                            <PlaylistAddIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="View relationship diagram" arrow>
                                        <IconButton
                                            size="small"
                                            aria-label={`view ${group.label} relationship diagram`}
                                            onClick={() => openDiagram(group.table)}
                                        >
                                            <AccountTreeIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </span>
                            </Box>
                        </ListSubheader>
                        {group.fields.map((f) => (
                            <ListItem key={f.columnKey} divider alignItems="flex-start">
                                <ListItemText
                                    primary={
                                        <span>
                                            <strong>{f.column}</strong>
                                            {f.uiLabel && (
                                                <Tooltip
                                                    title={f.uiLabelSource === 'curated' ? 'Curated label'
                                                        : f.uiLabelSource === 'screen' ? 'Label shown on this page'
                                                            : 'Label from app translations'}
                                                    arrow
                                                >
                                                    <span style={{ marginLeft: 6, fontStyle: 'italic', color: 'rgba(0,0,0,0.6)' }}>
                                                        {`“${f.uiLabel}”`}
                                                    </span>
                                                </Tooltip>
                                            )}
                                            <Chip
                                                size="small"
                                                label={f.sqlType || f.clrType}
                                                style={{ marginLeft: 6 }}
                                            />
                                            {f.verificationStatus === 'verified' && (
                                                <Chip size="small" color="primary" label="verified" style={{ marginLeft: 4 }} />
                                            )}
                                        </span>
                                    }
                                    secondary={
                                        <>
                                            <span>{f.description || '(no description yet)'}</span>
                                            {optionsSourceHref(f) ? (
                                                <Typography variant="caption" display="block">
                                                    <Link
                                                        variant="caption"
                                                        href={optionsSourceHref(f)}
                                                        target="_blank"
                                                        rel="noopener"
                                                    >
                                                        {optionsSourceText(f)}
                                                    </Link>
                                                </Typography>
                                            ) : optionsSourceShortLabel(f) && (
                                                <Typography variant="caption" display="block" color="textSecondary">
                                                    {optionsSourceShortLabel(f)}
                                                </Typography>
                                            )}
                                        </>
                                    }
                                />
                                <ListItemSecondaryAction>
                                    <BasketPinButton field={f} />
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </ul>
                </li>
            ))}
        </List>
    );

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                role: 'dialog',
                'aria-modal': true,
                'aria-labelledby': 'data-dictionary-title'
            }}
        >
            <Box ref={panelRef} style={{ width: 420, padding: 16 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography id="data-dictionary-title" variant="h6">Data Dictionary</Typography>
                    <IconButton size="small" onClick={onClose} aria-label="close data dictionary">
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Typography variant="body2" color="textSecondary" style={{ wordBreak: 'break-all' }}>
                    {location.pathname}
                </Typography>
                <Divider style={{ margin: '8px 0' }} />

                {loading && (
                    <Box display="flex" justifyContent="center" style={{ padding: 24 }}>
                        <CircularProgress size={28} />
                    </Box>
                )}

                {!loading && fields.length === 0 && (
                    <Box style={{ padding: 8 }}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            No data dictionary mapping for this page yet.
                        </Typography>
                        <Button
                            size="small"
                            color="primary"
                            onClick={() => { onClose(); history.push('/tools/data-dictionary'); }}
                        >
                            Browse all entities &rarr;
                        </Button>
                    </Box>
                )}

                {!loading && fields.length > 0 && (
                    <>
                        <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            placeholder="Search fields by name, label or description…"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ marginBottom: 8 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" color="action" />
                                    </InputAdornment>
                                )
                            }}
                        />

                        {onScreenFields.length > 0 && (
                            <>
                                <Typography variant="overline" color="primary" style={{ display: 'block' }}>
                                    On this screen ({onScreenFields.length})
                                </Typography>
                                {fieldList(onScreenFields)}
                                {otherFields.length > 0 && (
                                    <>
                                        <Divider style={{ margin: '8px 0' }} />
                                        <Typography variant="overline" color="textSecondary" style={{ display: 'block' }}>
                                            Other fields for this page
                                        </Typography>
                                    </>
                                )}
                            </>
                        )}

                        {otherFields.length > 0 && fieldList(otherFields)}

                        {/* term-gated: decorated lags fields by one render (the resolve effect),
                            so an ungated check would flash this message before the list appears */}
                        {term !== '' && visible.length === 0 && (
                            <Typography variant="body2" color="textSecondary" style={{ padding: 8 }}>
                                {`No fields match “${searchText}”.`}
                            </Typography>
                        )}
                    </>
                )}
            </Box>
        </Drawer>
    );
};

export default DataDictionaryPanel;
